import React, { useCallback, useMemo, useState } from 'react'
import {
  Button,
  Dropdown,
  Modal,
  ModalInjectedProps,
  Pill,
  Tooltip,
} from '@sketch/components'

import * as S from './ExportModal.styles'
import { ExportPreviewData, useExportPreview } from './useExportPreview'
import { ExportSettings } from '@sketch-hq/sketch-web-renderer'
import { v1 as uuid } from 'uuid'
import { ExportPrefixSuffixSettings, ExportSettingsItem } from './types'
import { ExportItem } from './ExportItem'
import {
  ANDROID_EXPORT_SETTINGS_PRESET,
  DEFAULT_EXPORT_SETTINGS,
  IOS_EXPORT_SETTINGS_PRESET,
} from './constants'
import { useThemeContext } from '@sketch/global-styles'

interface ExportModalProps extends ModalInjectedProps {
  exportAndDownload: (exportList: ExportSettingsItem[]) => Promise<void>
  getExportPreview: () => Promise<ExportPreviewData | null>
}

function createNewExportSettingsItem() {
  const prefixSuffix: ExportPrefixSuffixSettings = {
    type: 'suffix',
    content: '',
  }
  return {
    ...DEFAULT_EXPORT_SETTINGS,
    id: uuid(),
    prefixSuffix,
  }
}

type ExportsPreset = 'IOS' | 'ANDROID'
const presetMap: Record<ExportsPreset, ExportSettingsItem[]> = {
  IOS: IOS_EXPORT_SETTINGS_PRESET,
  ANDROID: ANDROID_EXPORT_SETTINGS_PRESET,
}
function createNewExportSettingsListFromPreset(
  preset: ExportsPreset
): ExportSettingsItem[] {
  // Doing a deep copy to avoid updating the preset.
  return presetMap[preset].map(item => JSON.parse(JSON.stringify(item)))
}

export function ExportModal({
  hideModal,
  exportAndDownload,
  getExportPreview,
}: ExportModalProps) {
  const [isExporting, setIsExporting] = useState(false)
  const [exportList, setExportList] = useState<ExportSettingsItem[]>([
    createNewExportSettingsItem(),
  ])

  const previewExportResult = useExportPreview(getExportPreview)

  const handleExport = useCallback(() => {
    if (exportList.length) {
      setIsExporting(true)
      // setTimeout used as a hack to show a loading state even
      // though UI is frozen.
      setTimeout(async () => {
        await exportAndDownload(exportList)
        setIsExporting(false)
        hideModal()
      }, 10)
    }
  }, [exportAndDownload, exportList, hideModal])

  const updateItem = useCallback(
    (id: string, update: Partial<ExportSettings>) => {
      setExportList(prevList => {
        const index = prevList.findIndex(item => item.id === id)
        if (index === -1) {
          return prevList
        }
        const newList = [...prevList]
        const newItem = { ...newList[index], ...update }
        newList[index] = newItem
        return newList
      })
    },
    []
  )

  const addItem = useCallback(() => {
    setExportList(prevList => [...prevList, createNewExportSettingsItem()])
  }, [])

  const removeItem = useCallback((id: string) => {
    setExportList(prevList => {
      return prevList.filter(item => item.id !== id)
    })
  }, [])

  // Get the unique list of file formats we are exporting.
  const formatTagList = useMemo(() => {
    const set = exportList.reduce((acc, item) => {
      acc.add(item.format)
      return acc
    }, new Set<string>())

    return Array.from(set)
  }, [exportList])

  const enableReset = useMemo(() => {
    const isInDefaultState =
      exportList.length === 1 &&
      exportList[0].format === DEFAULT_EXPORT_SETTINGS.format &&
      exportList[0].backingScale === DEFAULT_EXPORT_SETTINGS.backingScale &&
      exportList[0].prefixSuffix.content === ''
    return !isInDefaultState
  }, [exportList])

  const canvasPreviewRefHandler = useCallback(
    (canvas: HTMLCanvasElement) => {
      if (!canvas) return

      async function paintPreviewCanvas() {
        const context = canvas.getContext('2d')
        if (!context) {
          throw new Error('Could not get 2d context')
        }
        if (!previewExportResult) return
        const arrayBuffer = await previewExportResult.blob.arrayBuffer()
        const typedArray = new Uint8ClampedArray(arrayBuffer)
        const imageData = new ImageData(
          typedArray,
          previewExportResult.imageWidth,
          previewExportResult.imageHeight
        )
        // Draw full size image on canvas
        canvas.width = previewExportResult.imageWidth
        canvas.height = previewExportResult.imageHeight
        context.putImageData(imageData, 0, 0)

        // Resize canvas to fit the actual size of the layer.
        canvas.style.width = `${previewExportResult.layerWidth}px`
        canvas.style.height = `${previewExportResult.layerHeight}px`
      }

      paintPreviewCanvas()
    },
    [previewExportResult]
  )

  const { selectedTheme } = useThemeContext()

  return (
    <Modal autoFocusOnOpen={false} onCancel={hideModal}>
      <S.StyledModalHeader as="div">
        <S.PreviewContainer>
          {previewExportResult && (
            <S.StyledPill
              variant={selectedTheme === 'dark' ? 'secondary' : undefined}
            >
              {previewExportResult.layerWidth}
              {'x'}
              {previewExportResult.layerHeight}
            </S.StyledPill>
          )}
          <S.FormatTagListContainer>
            {formatTagList.map(format => {
              return (
                <Pill variant="secondary" key={format}>
                  {format.toUpperCase()}
                </Pill>
              )
            })}
          </S.FormatTagListContainer>
          <S.PreviewCanvasContainer>
            {!previewExportResult && <S.PreviewSkeleton />}
            {previewExportResult && (
              <S.PreviewCanvas ref={canvasPreviewRefHandler} />
            )}
          </S.PreviewCanvasContainer>
        </S.PreviewContainer>
      </S.StyledModalHeader>

      <S.StyledModalBody>
        <S.ExportModalSubHeader>
          <span>Export Formats</span>
          <S.HeaderActionsContainer>
            <Tooltip placement="top" content="Reset">
              <Button
                variant="ghost"
                disabled={!enableReset}
                size="32"
                onClick={() => setExportList([createNewExportSettingsItem()])}
              >
                <S.StyledArrowCircleIcon />
              </Button>
            </Tooltip>
            <Dropdown
              usePortal={true}
              placement="bottom-start"
              minWidth={'auto'}
              toggle={
                <Tooltip placement="top" content="Export presets">
                  <Button variant="ghost" size="32">
                    <S.StyledControlSwitchesIcon />
                  </Button>
                </Tooltip>
              }
            >
              <Dropdown.Item
                minWidth={'auto'}
                onClick={() =>
                  setExportList(createNewExportSettingsListFromPreset('IOS'))
                }
              >
                iOS
              </Dropdown.Item>
              <Dropdown.Item
                minWidth={'auto'}
                onClick={() =>
                  setExportList(
                    createNewExportSettingsListFromPreset('ANDROID')
                  )
                }
              >
                Android
              </Dropdown.Item>
            </Dropdown>
            <Tooltip placement="top" content="Add new Export option">
              <Button variant="ghost" onClick={addItem} size="32">
                <S.StyledPlusIcon />
              </Button>
            </Tooltip>
          </S.HeaderActionsContainer>
        </S.ExportModalSubHeader>

        <S.ExportItemsListContainer>
          {exportList.map(exportSetting => {
            return (
              <ExportItem
                key={exportSetting.id}
                updateItem={updateItem}
                exportSettingsItem={exportSetting}
                removeItem={removeItem}
                allowRemove={exportList.length > 1}
              />
            )
          })}
        </S.ExportItemsListContainer>
      </S.StyledModalBody>
      <Modal.Footer>
        <Button variant="secondary" onClick={hideModal}>
          Cancel
        </Button>
        <Button loading={isExporting} variant="primary" onClick={handleExport}>
          Export
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
